import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Password } from 'primereact/password';
import { User } from '../types';

interface UserFormProps {
  user?: User;
  onSubmit: (userData: { id?: number; email: string; password?: string; role: string }) => Promise<boolean>;
  onCancel: () => void;
  isEditMode: boolean;
}

const UserForm: React.FC<UserFormProps> = ({ user, onSubmit, onCancel, isEditMode }) => {
  const [formData, setFormData] = useState({
    id: user?.id || 0,
    email: user?.email || '',
    password: '',
    role: user?.role || 'viewer'
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const roles = [
    { label: 'Lectura', value: 'viewer' },
    { label: 'Edición', value: 'editor' },
    { label: 'Administrador', value: 'admin' }
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        id: user.id,
        email: user.email,
        password: '',
        role: user.role
      });
      setPasswordTouched(false);
    }
  }, [user]);

  // Validar contraseña y actualizar errores
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('La contraseña debe tener al menos 8 caracteres');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Debe contener al menos una letra mayúscula');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Debe contener al menos una letra minúscula');
    }
    
    if (!/[0-9\W]/.test(password)) {
      errors.push('Debe contener al menos un número o carácter especial');
    }
    
    return errors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'password') {
      setPasswordTouched(true);
      setPasswordErrors(validatePassword(value));
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (e: { value: string }) => {
    setFormData(prev => ({ ...prev, role: e.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);

    // Validación de email
    if (!formData.email) {
      return;
    }

    // Validación de contraseña (solo para crear o si se está editando y se ingresó una contraseña)
    if ((!isEditMode || formData.password) && passwordErrors.length > 0) {
      return;
    }

    // Validación adicional para modo creación
    if (!isEditMode && !formData.password) {
      return;
    }

    setLoading(true);

    // Si estamos en modo edición y no se ingresó contraseña, no la enviamos
    const submitData = isEditMode && !formData.password
      ? { id: formData.id, email: formData.email.trim(), role: formData.role }
      : formData;

    const success = await onSubmit(submitData);
    
    if (success) {
      onCancel(); // Cerrar formulario si fue exitoso
    }
    
    setLoading(false);
  };

  // Mostrar feedback de contraseña siempre que esté tocado o enviado
  const showPasswordFeedback = passwordTouched || submitted;

  // Determinar si el botón debe estar deshabilitado
  const isButtonDisabled = (!isEditMode && (!formData.password || passwordErrors.length > 0)) || 
                           (formData.password !== '' && passwordErrors.length > 0);

  return (
    <div className="user-form">
      <h2>{isEditMode ? 'Editar Usuario' : 'Crear Usuario'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="email">Email</label>
          <InputText
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={submitted && !formData.email ? 'p-invalid' : ''}
            placeholder="Correo electrónico"
            required
          />
          {submitted && !formData.email && <small className="p-error">Email es requerido</small>}
        </div>

        <div className="field">
          <label htmlFor="password">Contraseña{isEditMode && ' (Dejar en blanco para mantener)'}</label>
          <Password
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            onFocus={() => setPasswordTouched(true)}
            className={(submitted && !formData.password && !isEditMode) || 
                      (showPasswordFeedback && passwordErrors.length > 0 && formData.password) 
                      ? 'p-invalid' : ''}
            placeholder="Contraseña"
            toggleMask
            feedback={false}
          />
          {submitted && !formData.password && !isEditMode && 
            <small className="p-error">Contraseña es requerida</small>}
          
          {/* Mostrar errores de validación de contraseña */}
          {showPasswordFeedback && formData.password && passwordErrors.length > 0 && (
            <div className="password-errors">
              {passwordErrors.map((error, index) => (
                <small key={index} className="p-error d-block">{error}</small>
              ))}
            </div>
          )}
        </div>

        <div className="field">
          <label htmlFor="role">Rol</label>
          <Dropdown
            id="role"
            name="role"
            value={formData.role}
            options={roles}
            onChange={handleRoleChange}
            placeholder="Seleccionar rol"
          />
        </div>

        <div className="form-actions">
          <Button 
            type="submit" 
            label={isEditMode ? 'Actualizar' : 'Crear'} 
            icon="pi pi-save" 
            loading={loading}
            disabled={isButtonDisabled}
          />
          <Button 
            type="button" 
            label="Cancelar" 
            icon="pi pi-times" 
            className="p-button-secondary" 
            onClick={onCancel}
          />
        </div>
      </form>
    </div>
  );
};

export default UserForm; 